import { useEffect, useRef, useState } from 'react';
import {
  TenantThemeProvider, fetchPublicTenant, tenantIntakeQuestions,
  useTenant, DEFAULT_TENANT, type TenantConfig, type IntakeQuestion,
} from '@/lib/tenant';
import { captureLead, type LeadFields } from '@/lib/api';
import IntakeFlow, { type IntakeFlowHandle } from '@/components/IntakeFlow';
import modal from '@/components/Modal.module.css';

/**
 * Standalone public intake → quote front door (white-label §0.5).
 *
 * The firm hands this URL (their own domain + /#/start) to prospects. Unlike the
 * marketing IntakeModal — which opens over the AVOWAL site — this is a full
 * STANDALONE page rendered OUTSIDE MarketingLayout: no AVOWAL Nav, no AVOWAL
 * Footer, no marketing modals. It resolves the tenant by HOST (fetchPublicTenant,
 * since there's no session yet) and paints ONLY the firm's brand + named
 * assistant + configured question set, degrading to AVOWAL-neutral for the apex.
 *
 * Mirrors VerifyEmail.tsx / MeetYourLawyer.tsx: a chrome-free, deep-link surface.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function TenantIntake() {
  const [tenant, setTenant] = useState<TenantConfig>(DEFAULT_TENANT);
  useEffect(() => {
    let live = true;
    void fetchPublicTenant().then((t) => { if (live) setTenant(t); });
    return () => { live = false; };
  }, []);
  return (
    <TenantThemeProvider tenant={tenant}>
      <IntakePage questions={tenantIntakeQuestions(tenant)} />
    </TenantThemeProvider>
  );
}

function IntakePage({ questions }: { questions: IntakeQuestion[] }) {
  const { displayName } = useTenant();
  // Shared handle so the abandoned-lead effect can persist a partial lead if the
  // prospect leaves without a successful submit (mirrors the modal path).
  const handleRef = useRef<IntakeFlowHandle>({ fields: { email: '' }, done: false });
  const [instance, setInstance] = useState(0);
  useEffect(() => () => {
    const h = handleRef.current;
    if (!h.done && EMAIL_RE.test((h.fields.email ?? '').trim())) {
      void captureLead(h.fields as LeadFields, false);
    }
  }, []);
  // "Close" on the standalone page has nowhere firm-branded to go, so it just
  // resets the flow (fresh state) rather than leaking back to the AVOWAL home.
  const reset = () => {
    handleRef.current = { fields: { email: '' }, done: false };
    setInstance((n) => n + 1);
  };
  return (
    <div style={{
      minHeight: '100vh', background: 'var(--navy)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '3rem 1rem 5rem',
    }}>
      <header style={{ marginBottom: '1.75rem', textAlign: 'center' }}>
        <div style={{
          fontFamily: 'var(--font-display)', fontSize: '1.9rem', letterSpacing: '0.16em',
          color: 'var(--text)',
        }}>
          {displayName}
        </div>
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: '0.62rem', letterSpacing: '0.24em',
          textTransform: 'uppercase', color: 'var(--cyan)', marginTop: '0.35rem',
        }}>
          Start your matter
        </div>
      </header>
      <div className={modal.modal} style={{ boxShadow: '0 40px 80px rgba(0, 0, 0, 0.45)' }}>
        <IntakeFlow
          key={instance}
          questions={questions}
          handleRef={handleRef}
          onClose={reset}
        />
      </div>
    </div>
  );
}
